import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDate } from '../lib/utils';

const box = { background: '#161d31', borderRadius: 12, padding: 16, marginBottom: 16 };
const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 14 };
const subtleBtn = { ...btn, background: '#334155' };

const badgeBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1,
  border: '1px solid transparent',
};

function quizModeLabel(quiz) {
  if (quiz.generated_template_type === 'rule') return { text: 'Generated', style: { background: '#312e81', color: '#c7d2fe', borderColor: '#4f46e5' } };
  if (quiz.generated_template_type === 'fixed') return { text: 'From Template', style: { background: '#0f766e', color: '#ccfbf1', borderColor: '#14b8a6' } };
  return { text: 'Manual', style: { background: '#1f2937', color: '#cbd5e1', borderColor: '#475569' } };
}

function SnapshotBadge({ count }) {
  if (!count) return null;
  return (
    <span style={{ ...badgeBase, background: '#14532d', color: '#bbf7d0', borderColor: '#16a34a' }}>
      Frozen · {count} snapshot{count === 1 ? '' : 's'}
    </span>
  );
}

export default function QuizListPage() {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreError, setRestoreError] = useState('');
  const [restoreSuccess, setRestoreSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [quizzesData, templatesData] = await Promise.all([api.quizzes(), api.templates()]);
      setQuizzes(quizzesData);
      setTemplates(templatesData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function restoreFromTemplate(templateId) {
    setRestoring(templateId);
    setRestoreError('');
    try {
      const newTitle = `Copy of ${templates.find(t => t.id === templateId)?.name || 'Template'}`;
      const newQuiz = await api.restoreTemplate(templateId, newTitle);
      setRestoreSuccess(`Created "${newTitle}"`);
      setTimeout(() => {
        navigate(`/quizzes/${newQuiz.id}`);
      }, 800);
    } catch (err) {
      setRestoreError(err.message);
      setRestoring(null);
    }
  }

  async function handleDeleteQuiz(quizId, quizTitle) {
    if (!window.confirm(`Delete quiz "${quizTitle}"? This cannot be undone.`)) return;
    setDeleting(quizId);
    try {
      await api.deleteQuiz(quizId);
      setQuizzes(prev => prev.filter(q => q.id !== quizId));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(null);
    }
  }

  if (loading) return <div style={{ padding: 20 }}>Loading quizzes...</div>;

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <h1>Quizzes</h1>
      {error && <div style={{ background: '#450a0a', color: '#fca5a5', padding: 12, borderRadius: 6, marginBottom: 16 }}>Error: {error}</div>}

      <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
        <Link to="/quizzes/new" style={{ ...btn, textDecoration: 'none' }}>+ Create New Quiz</Link>
        {templates.length > 0 && (
          <button onClick={() => setShowRestoreModal(true)} style={subtleBtn}>
            ⚡ Restore from Template ({templates.length})
          </button>
        )}
      </div>

      <div style={{ ...box, background: '#0f172a', border: '1px solid #334155' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Quiz types at a glance</div>
        <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>
          Generated and template-based quizzes can carry <strong style={{ color: '#e2e8f0' }}>frozen snapshots</strong>.
          When snapshots exist, exports use that frozen content instead of the current source question.
        </div>
      </div>

      {showRestoreModal && templates.length > 0 && (
        <div style={{ ...box, border: '2px solid #4f46e5' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Restore from Template</h3>
            <button onClick={() => { setShowRestoreModal(false); setRestoreError(''); }} style={{ ...subtleBtn, padding: '6px 12px', fontSize: 12 }}>✕</button>
          </div>
          {restoreError && <div style={{ background: '#450a0a', color: '#fca5a5', padding: 12, borderRadius: 6, marginBottom: 12 }}>{restoreError}</div>}
          {restoreSuccess && <div style={{ background: '#052e16', color: '#86efac', padding: 12, borderRadius: 6, marginBottom: 12 }}>✓ {restoreSuccess}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {templates.map(template => (
              <div key={template.id} style={{ background: '#0f172a', padding: 12, borderRadius: 8, border: '1px solid #334155', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontWeight: 600, marginBottom: 4, flex: 1 }}>{template.name}</div>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ ...badgeBase, ...(template.template_type === 'rule' ? { background: '#312e81', color: '#c7d2fe', borderColor: '#4f46e5' } : { background: '#0f766e', color: '#ccfbf1', borderColor: '#14b8a6' }) }}>
                    {template.template_type === 'rule' ? 'Rule-based template' : 'Fixed template'}
                  </span>
                </div>
                {template.description && <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8, flex: 1 }}>{template.description}</div>}
                <button
                  onClick={() => restoreFromTemplate(template.id)}
                  disabled={restoring === template.id}
                  style={{ ...btn, fontSize: 12, padding: '6px 12px', cursor: restoring === template.id ? 'wait' : 'pointer' }}
                >
                  {restoring === template.id ? 'Restoring…' : 'Restore'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {quizzes.length === 0 ? (
        <div style={box}>
          <p>No quizzes yet. <Link to="/quizzes/new" style={{ color: '#93c5fd', textDecoration: 'none' }}>Create one</Link> or <button onClick={() => setShowRestoreModal(true)} style={{ ...btn, padding: '4px 8px', fontSize: 12 }}>restore from a template</button>.</p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #334155' }}>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: '#94a3b8' }}>Title</th>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: '#94a3b8' }}>Workflow</th>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: '#94a3b8' }}>Status</th>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: '#94a3b8' }}>Questions</th>
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: '#94a3b8' }}>Updated</th>
              <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, color: '#94a3b8' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {quizzes.map(quiz => {
              const mode = quizModeLabel(quiz);
              return (
                <tr key={quiz.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '12px 8px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <Link to={`/quizzes/${quiz.id}`} style={{ color: '#93c5fd', textDecoration: 'none' }}>{quiz.title}</Link>
                      {quiz.generated_template_name && (
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>Source template: {quiz.generated_template_name}</div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <span style={{ ...badgeBase, ...mode.style }}>{mode.text}</span>
                      <SnapshotBadge count={quiz.snapshot_count} />
                    </div>
                  </td>
                  <td style={{ padding: '12px 8px', fontSize: 12, color: '#94a3b8' }}>{quiz.status}</td>
                  <td style={{ padding: '12px 8px', fontSize: 12 }}>{quiz.item_count || 0} items</td>
                  <td style={{ padding: '12px 8px', fontSize: 12, color: '#94a3b8' }}>{formatDate(quiz.updated_at)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                    <Link to={`/quizzes/${quiz.id}`} style={{ color: '#93c5fd', textDecoration: 'none', marginRight: 12 }}>View</Link>
                    <Link to={`/quizzes/${quiz.id}/edit`} style={{ color: '#93c5fd', textDecoration: 'none', marginRight: 12 }}>Edit</Link>
                    <Link to={`/quizzes/${quiz.id}/export`} style={{ color: '#93c5fd', textDecoration: 'none', marginRight: 12 }}>Export</Link>
                    <button
                      onClick={() => handleDeleteQuiz(quiz.id, quiz.title)}
                      disabled={deleting === quiz.id}
                      style={{ color: '#fca5a5', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}
                    >
                      {deleting === quiz.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
